import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  Timestamp,
  serverTimestamp
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../config/firebase';
import { TicketTier } from '../types/models';
import { toast } from 'sonner';

export interface EventLocation {
  address: string;
  lat: number;
  lng: number;
}

export interface EventData {
  title: string;
  description: string;
  startDateTime: Date;
  endDateTime?: Date;
  location: EventLocation;
  price?: number; // Optional now since we have ticket tiers
  capacity: number;
  organizerId: string;
  organizerName: string;
  organizerPhotoURL?: string;
  coverImage?: string;
  tags?: string[];
  ticketTiers?: TicketTier[]; // Added ticket tiers field
  isRecurring?: boolean;
  recurringPattern?: 'daily' | 'weekly' | 'monthly';
  recurringEndDate?: Date;
  status: 'draft' | 'published' | 'cancelled' | 'pending_review';
  category: string; // Added category
  visibility: 'public' | 'private' | 'unlisted'; // Added visibility
  createdAt?: Date;
  updatedAt?: Date;
}

export interface Event extends EventData {
  id: string;
}

const isOnline = () => {
  return typeof navigator !== 'undefined' && navigator.onLine;
};

const handleFirestoreError = (error: any): never => {
  console.error('Firestore error:', error);
  throw new Error('Failed to process your request. Please try again later.');
};

// Validation functions
const validateEventData = (eventData: any) => {
  const errors = [];
  
  if (!eventData.title?.trim()) errors.push('Event title is required');
  if (!eventData.description?.trim()) errors.push('Event description is required');
  if (!eventData.startDateTime) errors.push('Start date and time are required');
  if (!eventData.location?.address) errors.push('Event location is required');
  if (!eventData.ticketTiers?.length) errors.push('At least one ticket tier is required');
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

// Error handling function
const handleEventError = (error: any): never => {
  console.error('Event creation error:', error);
  
  // Handle specific Firebase errors
  if (error.code === 'permission-denied') {
    throw new Error('You do not have permission to create events. Please contact support.');
  }
  
  if (error.code === 'unavailable') {
    throw new Error('The service is currently unavailable. Please try again later.');
  }
  
  // Handle validation errors
  if (error.validation) {
    throw new Error(error.validation.join(', '));
  }
  
  throw new Error('Failed to create event. Please try again.');
};

export const createEvent = async (eventData: any, imageFile?: File): Promise<string> => {
  try {
    // Validate event data
    const validation = validateEventData(eventData);
    if (!validation.isValid) {
      throw { validation: validation.errors };
    }

    // Check user permissions and get user data
    if (!eventData.organizerId) {
      throw new Error('Organizer ID is required');
    }

    const userDoc = await getDoc(doc(db, 'users', eventData.organizerId));
    if (!userDoc.exists()) {
      throw new Error('User not found');
    }

    const userData = userDoc.data();
    if (!userData?.roles?.includes('organizer')) {
      throw new Error('You do not have permission to create events');
    }

    let coverImage = eventData.coverImage;

    // Handle image upload if provided
    if (imageFile) {
      try {
        const imageRef = ref(storage, `events/${Date.now()}_${imageFile.name}`);
        await uploadBytes(imageRef, imageFile);
        coverImage = await getDownloadURL(imageRef);
      } catch (error) {
        console.error('Image upload error:', error);
        throw new Error('Failed to upload event image. Please try again.');
      }
    }

    // Set initial status based on user role
    let status = 'pending_review';
    if (userData.roles.includes('admin')) {
      status = 'published';
    }

    const transformedData = {
      ...eventData,
      coverImage,
      status,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      startDateTime: eventData.startDateTime instanceof Date 
        ? Timestamp.fromDate(eventData.startDateTime)
        : eventData.startDateTime,
      endDateTime: eventData.endDateTime instanceof Date 
        ? Timestamp.fromDate(eventData.endDateTime)
        : eventData.endDateTime,
    };

    // Create event document
    const eventRef = await addDoc(collection(db, 'events'), transformedData);
    
    // Show appropriate success message based on status
    if (status === 'pending_review') {
      toast.success('Event created successfully! It will be published after admin review.');
    } else {
      toast.success('Event created and published successfully!');
    }
    
    return eventRef.id;
  } catch (error) {
    return handleEventError(error);
  }
};

export const updateEvent = async (eventId: string, eventData: Partial<EventData>, imageFile?: File): Promise<void> => {
  try {
    if (!isOnline()) {
      throw new Error('You are currently offline. Please check your internet connection and try again.');
    }

    if (!db || !storage) {
      throw new Error('Firebase is not initialized');
    }

    const eventRef = doc(db, 'events', eventId);
    const eventDoc = await getDoc(eventRef);

    if (!eventDoc.exists()) {
      throw new Error('Event not found');
    }

    // Check if user is trying to publish event
    if (eventData.status === 'published' && eventData.organizerId) {
      const userDoc = await getDoc(doc(db, 'users', eventData.organizerId));
      if (!userDoc.exists() || !userDoc.data()?.roles?.includes('admin')) {
        throw new Error('Only admins can publish events. Your event will be published after admin review.');
      }
    }

    let coverImage = eventData.coverImage;

    // Upload new image if provided
    if (imageFile) {
      // Delete old image if exists
      if (eventDoc.data().coverImage) {
        try {
          const oldImageRef = ref(storage, eventDoc.data().coverImage);
          await deleteObject(oldImageRef);
        } catch (error) {
          console.error('Error deleting old image:', error);
        }
      }

      const imageRef = ref(storage, `events/${Date.now()}_${imageFile.name}`);
      await uploadBytes(imageRef, imageFile);
      coverImage = await getDownloadURL(imageRef);
    }

    await updateDoc(eventRef, {
      ...eventData,
      coverImage,
      updatedAt: serverTimestamp(),
      startDateTime: eventData.startDateTime ? Timestamp.fromDate(eventData.startDateTime) : undefined,
      endDateTime: eventData.endDateTime ? Timestamp.fromDate(eventData.endDateTime) : null,
      recurringEndDate: eventData.recurringEndDate ? Timestamp.fromDate(eventData.recurringEndDate) : null
    });
  } catch (error) {
    handleFirestoreError(error);
  }
};

export const deleteEvent = async (eventId: string): Promise<void> => {
  if (!isOnline()) {
    throw new Error('You are currently offline. Please check your internet connection.');
  }
  
  const eventRef = doc(db, 'events', eventId);

  try {
    const eventDoc = await getDoc(eventRef);
    if (!eventDoc.exists()) {
      toast.info("Event not found or already deleted.");
      return;
    }

    // Delete cover image from Storage if it exists
    const coverImageURL = eventDoc.data().coverImage;
    if (coverImageURL && typeof coverImageURL === 'string') {
      try {
        const imageRef = ref(storage, coverImageURL);
        await deleteObject(imageRef);
      } catch (storageError: any) {
        // Log the error, but don't block deletion if the image is already gone
        if (storageError.code !== 'storage/object-not-found') {
          console.warn('Could not delete event image:', storageError);
        }
      }
    }

    // Delete the event document from Firestore
    await deleteDoc(eventRef);

  } catch (error) {
    console.error('Error deleting event:', error);
    toast.error('An unexpected error occurred while deleting the event.');
    // Re-throw the error to be caught by the calling component's try-catch block
    throw new Error('Failed to delete event.');
  }
};

export const getEventById = async (eventId: string): Promise<Event> => {
  if (!eventId) {
    throw new Error('Event ID is required');
  }

  const eventRef = doc(db, 'events', eventId);
  const eventDoc = await getDoc(eventRef);

  if (!eventDoc.exists()) {
    throw new Error('Event not found');
  }

  const data = eventDoc.data();

  // Convert Firestore Timestamps to Date objects if they exist
  const safeToDate = (timestamp: any) => {
    return timestamp && timestamp instanceof Timestamp ? timestamp.toDate() : timestamp;
  };

  return {
    id: eventDoc.id,
    title: data.title,
    description: data.description,
    startDateTime: safeToDate(data.startDateTime),
    endDateTime: safeToDate(data.endDateTime),
    location: data.location,
    price: data.price,
    capacity: data.capacity,
    organizerId: data.organizerId,
    organizerName: data.organizerName,
    organizerPhotoURL: data.organizerPhotoURL,
    coverImage: data.coverImage,
    tags: data.tags || [],
    ticketTiers: data.ticketTiers || [],
    isRecurring: data.isRecurring || false,
    recurringPattern: data.recurringPattern || undefined,
    recurringEndDate: safeToDate(data.recurringEndDate),
    status: data.status,
    category: data.category,
    visibility: data.visibility,
    createdAt: safeToDate(data.createdAt),
    updatedAt: safeToDate(data.updatedAt),
  };
};

export const getAllPublishedEvents = async (): Promise<Partial<Event>[]> => {
  try {
    if (!isOnline()) {
      throw new Error('You are currently offline. Please check your internet connection.');
    }

    const eventsQuery = query(collection(db, 'events'), where('status', '==', 'published'));
    const querySnapshot = await getDocs(eventsQuery);

    const events = querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        title: data.title,
        description: data.description,
        category: data.category,
        location: data.location,
        startDateTime: data.startDateTime?.toDate(),
      };
    });

    return events;
  } catch (error) {
    handleFirestoreError(error);
    return [];
  }
};

export const getEventsByOrganizer = async (organizerId: string): Promise<Event[]> => {
  try {
    if (!isOnline()) {
      throw new Error('You are currently offline. Please check your internet connection and try again.');
    }

    if (!db) {
      throw new Error('Firestore is not initialized');
    }

    const eventsQuery = query(
      collection(db, 'events'),
      where('organizerId', '==', organizerId),
      orderBy('startDateTime', 'desc')
    );

    const querySnapshot = await getDocs(eventsQuery);
    const events = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      startDateTime: doc.data().startDateTime.toDate(),
      endDateTime: doc.data().endDateTime?.toDate(),
      recurringEndDate: doc.data().recurringEndDate?.toDate(),
      createdAt: doc.data().createdAt?.toDate(),
      updatedAt: doc.data().updatedAt?.toDate()
    })) as Event[];

    return events;
  } catch (error) {
    handleFirestoreError(error);
    return [];
  }
}; 